/* Delivery choices are saved on the existing Shopify cart, never on a cloned checkout. */
(() => {
  if (window.TakebagDelivery) return;
  const root = window.Shopify?.routes?.root || '/';
  const endpoint = root + 'api/2026-07/graphql.json';
  const destinationKey = 'takebag.delivery.destination.v1', preferenceKey = 'takebag.delivery.choice.v1';
  const read = key => { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (_) { return null; } };
  const write = (key, value) => { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) {} };
  const digits = value => String(value || '').replace(/\D/g, '');
  let cache, pending, chain = Promise.resolve(), busy = false, checkoutButton = null, checkoutButtonText = '';
  const RATE_QUERY = 'query TakebagCartDelivery($id: ID!) { cart(id:$id) { id checkoutUrl delivery { addresses { selected address { ... on CartDeliveryAddress { zip countryCode provinceCode } } } } ...TakebagChoices @defer } } fragment TakebagChoices on Cart { deliveryGroups(first:10,withCarrierRates:true) { nodes { id deliveryOptions { handle code title description estimatedCost { amount currencyCode } } selectedDeliveryOption { handle } } pageInfo { hasNextPage endCursor } } }';
  const ADDRESS_MUTATION = 'mutation TakebagCartDestination($id: ID!, $addresses: [CartSelectableAddressInput!]!) { cartDeliveryAddressesAdd(cartId:$id,addresses:$addresses) { cart { id } userErrors { field message } } }';
  const CHOICE_MUTATION = 'mutation TakebagCartChoice($id: ID!, $choices: [CartSelectedDeliveryOptionInput!]!) { cartSelectedDeliveryOptionsUpdate(cartId:$id,selectedDeliveryOptions:$choices) { cart { id checkoutUrl deliveryGroups(first:10) { nodes { id selectedDeliveryOption { handle } } pageInfo { hasNextPage endCursor } } } userErrors { field message } } }';
  async function api(query, variables) {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(endpoint, {method:'POST',credentials:'omit',signal:controller.signal,headers:{'Content-Type':'application/json',Accept:'multipart/mixed; deferSpec=20220824, application/json'},body:JSON.stringify({query,variables})});
      if (!response.ok) throw new Error('delivery-api');
      const body = await response.text(), type = response.headers.get('content-type') || '';
      const boundary = type.match(/boundary="?([^";\s]+)"?/)?.[1];
      const parts = type.includes('multipart/mixed') && boundary ? body.split('--'+boundary).filter(part=>part.includes('{')).map(part=>JSON.parse(part.slice(part.indexOf('{'),part.lastIndexOf('}')+1))) : [JSON.parse(body)];
      let data = {};
      for (const part of parts) {
        if (part.errors?.length) throw new Error('delivery-api');
        Object.assign(data,part.data);
        for (const inc of part.incremental || []) {
          if (inc.errors?.length) throw new Error('delivery-api');
          if (inc.path?.join('.') === 'cart') Object.assign(data.cart,inc.data);
        }
      }
      return data;
    } finally { clearTimeout(timer); }
  }
  async function nativeCart() {
    const response = await fetch(root+'cart.js',{credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}});
    if (!response.ok) throw new Error('cart');
    return response.json();
  }
  const fingerprint = cart => JSON.stringify([cart.token,cart.items.map(item=>[item.key,item.quantity,item.final_line_price]),cart.total_price,cart.discount_codes]);
  const match = (rate, choice) => !!choice && (choice.code ? rate.code === choice.code : rate.name === choice.name);
  function preference(zip) { const choice = read(preferenceKey); return choice?.zip === zip ? choice : null; }
  function choose(rate, zip) {
    write(preferenceKey,{zip,code:rate.code || '',name:rate.name,price:Number(rate.price),at:Date.now()});
    document.dispatchEvent(new CustomEvent('takebag:delivery-choice',{detail:{zip}}));
  }
  async function performQuote(destination) {
    const cart = await nativeCart();
    if (!cart.items.length || !cart.requires_shipping) return null;
    const key = fingerprint(cart), zip = digits(destination.zip);
    let quote;
    if (cache && cache.key === key && cache.zip === zip && Date.now()-cache.at < 120000) quote = cache;
    else {
      const id = 'gid://shopify/Cart/'+cart.token;
      let data = await api(RATE_QUERY,{id});
      if (!data.cart) throw new Error('cart-unavailable');
      const selectedAddress = data.cart.delivery.addresses.find(address=>address.selected)?.address;
      if (digits(selectedAddress?.zip) !== zip || selectedAddress?.countryCode !== 'BR') {
        const update = (await api(ADDRESS_MUTATION,{id,addresses:[{address:{deliveryAddress:{zip,provinceCode:destination.province,countryCode:'BR'}},selected:true,oneTimeUse:true}]})).cartDeliveryAddressesAdd;
        if (!update?.cart || update.userErrors.length) throw new Error('address');
        data = await api(RATE_QUERY,{id});
      }
      const groups = data.cart.deliveryGroups;
      if (groups?.nodes.length !== 1 || groups.pageInfo.hasNextPage) throw new Error('delivery-groups');
      const group = groups.nodes[0];
      const rates = group.deliveryOptions.filter(rate=>Number.isFinite(Number(rate.estimatedCost.amount))).map(rate=>({handle:rate.handle,code:rate.code,name:rate.title,description:rate.description,price:Number(rate.estimatedCost.amount),currency:rate.estimatedCost.currencyCode}));
      if (!rates.length) throw new Error('no-rates');
      quote = {key,zip,id,groupId:group.id,rates,selected:group.selectedDeliveryOption?.handle,checkoutUrl:data.cart.checkoutUrl,at:Date.now()};
      cache = quote;
    }
    const wanted = preference(zip), rate = quote.rates.find(option=>match(option,wanted));
    if (rate && quote.selected !== rate.handle) {
      const result = (await api(CHOICE_MUTATION,{id:quote.id,choices:[{deliveryGroupId:quote.groupId,deliveryOptionHandle:rate.handle}]})).cartSelectedDeliveryOptionsUpdate;
      if (!result?.cart || result.userErrors.length || result.cart.deliveryGroups.nodes[0]?.selectedDeliveryOption?.handle !== rate.handle) throw new Error('selection');
      quote.selected = rate.handle;
      quote.checkoutUrl = result.cart.checkoutUrl;
    }
    // Never expose a quote for a cart that changed while carrier rates were loading.
    if (fingerprint(await nativeCart()) !== key) { cache = null; throw new Error('cart-changed'); }
    return quote;
  }
  function quote(destination) {
    const requestKey = JSON.stringify([destination,preference(digits(destination.zip))]);
    if (pending?.key === requestKey) return pending.promise;
    const promise = chain.catch(()=>{}).then(()=>performQuote(destination));
    chain = promise;
    pending = {key:requestKey,promise};
    promise.finally(()=>{if(pending?.promise === promise)pending=null;}).catch(()=>{});
    return promise;
  }
  function checkoutFallback(destination) {
    const url = new URL(root+'checkout',location.origin);
    url.searchParams.set('checkout[shipping_address][zip]',destination.zip);
    url.searchParams.set('checkout[shipping_address][province]',destination.province);
    url.searchParams.set('checkout[shipping_address][country]','BR');
    return url;
  }
  function withDiscount(url) {
    let code; try { code = sessionStorage.getItem('bundle_discount_code'); } catch (_) {}
    return code ? root+'discount/'+encodeURIComponent(code)+'?redirect='+encodeURIComponent(url.pathname+url.search) : url.href;
  }
  async function checkout(event, button) {
    const destination = read(destinationKey);
    if (!/^\d{8}$/.test(destination?.zip || '') || !destination.province) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy) return;
    busy = true;
    const original = button?.innerHTML;
    checkoutButton=button;checkoutButtonText=original;
    if (button) {button.disabled=true;button.setAttribute('aria-busy','true');button.textContent='CONFIRMANDO FRETE...';}
    document.querySelectorAll('[data-tb-checkout-error]').forEach(node=>node.remove());
    try {
      if (window.TakebagCartMutation) await window.TakebagCartMutation;
      const result = await quote(destination);
      const url = result ? new URL(result.checkoutUrl) : checkoutFallback(destination);
      if (url.origin !== location.origin) throw new Error('checkout-origin');
      location.assign(withDiscount(url));
    } catch (_) {
      const message = document.createElement('p'); message.dataset.tbCheckoutError='';message.setAttribute('role','status');message.className='tb-shipping__status';
      message.append('Não conseguimos confirmar o frete agora. Tente novamente ou ');
      const link = document.createElement('a');link.dataset.tbNativeCheckout='';link.href=withDiscount(checkoutFallback(destination));link.textContent='continue no checkout';link.style.textDecoration='underline';
      message.append(link,'.'); (button?.parentElement || document.querySelector('main')).append(message);
      busy=false;
      if(button){button.disabled=false;button.removeAttribute('aria-busy');button.innerHTML=original;}
    }
  }
  window.addEventListener('submit',event=>{
    const form=event.target;
    if(form instanceof HTMLFormElement && (event.submitter?.name === 'checkout' || form.querySelector('input[name="checkout"]'))) checkout(event,event.submitter || document.querySelector('button[form="'+form.id+'"][name="checkout"]'));
  },true);
  window.addEventListener('click',event=>{
    const link=event.target.closest?.('a[href]');
    if(!link || link.hasAttribute('data-tb-native-checkout') || event.ctrlKey || event.metaKey || event.shiftKey || event.button) return;
    const url=new URL(link.href,location.origin);
    if(url.origin===location.origin && /\/checkout\/?$/.test(url.pathname)) checkout(event,link);
  },true);
  window.addEventListener('pageshow',event=>{if(event.persisted){busy=false;cache=null;if(checkoutButton){checkoutButton.disabled=false;checkoutButton.removeAttribute('aria-busy');checkoutButton.innerHTML=checkoutButtonText;}}});
  document.addEventListener('cart:updated',()=>{cache=null;});
  document.addEventListener('cart:refresh',()=>{cache=null;});
  window.TakebagDelivery={quote,choose,preference,match};
  document.dispatchEvent(new CustomEvent('takebag:delivery-ready'));
})();

